import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import SecurityHeader from '../../components/ui/SecurityHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NotificationBanner from '../../components/ui/NotificationBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import all components
import UserManagementSection from './components/UserManagementSection';
import AccessPolicySection from './components/AccessPolicySection';
import SystemConfigSection from './components/SystemConfigSection';
import PWAConfigSection from './components/PWAConfigSection';
import WebSocketConfigSection from './components/WebSocketConfigSection';
import DeviceManagementSection from './components/DeviceManagementSection'; // ✅ NEW

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState('devices'); // ✅ Changed default
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const settingSections = [
    // ✅ NEW: Device Management as first section
    {
      id: 'devices',
      label: 'Device Management',
      icon: 'Smartphone',
      description: 'Manage devices, PIN, cards, and master cards',
      component: DeviceManagementSection
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'Users',
      description: 'Manage administrator and viewer accounts',
      component: UserManagementSection
    },
    {
      id: 'policies',
      label: 'Access Policies',
      icon: 'Shield',
      description: 'Configure security policies and restrictions',
      component: AccessPolicySection
    },
    {
      id: 'system',
      label: 'System Config',
      icon: 'Settings',
      description: 'System-wide configuration and preferences',
      component: SystemConfigSection
    },
    {
      id: 'pwa',
      label: 'PWA Settings',
      icon: 'Monitor',
      description: 'Progressive Web App configuration',
      component: PWAConfigSection
    },
    {
      id: 'websocket',
      label: 'Real-time Config',
      icon: 'Radio',
      description: 'WebSocket and real-time communication',
      component: WebSocketConfigSection
    }
  ];

  const handleSectionChange = (sectionId) => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this section?');
      if (!confirmLeave) return;
    }
    
    setActiveSection(sectionId);
    setHasUnsavedChanges(false);
  };

  const handleExportSettings = () => {
    const settingsData = {
      exportDate: new Date().toISOString(),
      version: '1.0.0',
      settings: {
        deviceManagement: 'Device management settings',
        userManagement: 'User management settings',
        accessPolicies: 'Access policy settings',
        systemConfig: 'System configuration',
        pwaConfig: 'PWA configuration',
        websocketConfig: 'WebSocket configuration'
      }
    };

    const blob = new Blob([JSON.stringify(settingsData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `securelock-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const ActiveComponent = settingSections.find(section => section.id === activeSection)?.component;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Settings - SecureLock Admin</title>
        <meta name="description" content="Configure system settings, user management, and security policies for SecureLock Admin dashboard" />
      </Helmet>
      <SecurityHeader />
      <NotificationBanner />
      <main className="pt-16 pb-20 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center space-x-3">
                <Icon name="Settings" size={28} className="text-primary" />
                <span>Settings</span>
              </h1>
              <p className="text-muted-foreground mt-2">
                Configure system settings, manage devices, users, and customize security policies
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportSettings}
                className="security-transition"
              >
                Export Settings
              </Button>
              
              {hasUnsavedChanges && (
                <div className="flex items-center space-x-2 text-warning">
                  <Icon name="AlertCircle" size={16} />
                  <span className="text-sm">Unsaved changes</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Settings Navigation - Desktop Sidebar */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-card rounded-lg border border-border p-4 security-shadow-card sticky top-24">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center space-x-2">
                  <Icon name="List" size={16} className="text-primary" />
                  <span>Configuration Sections</span>
                </h3>
                <nav className="space-y-2">
                  {settingSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id)}
                      className={`
                        w-full text-left p-3 rounded-lg security-transition
                        ${activeSection === section.id 
                          ? 'bg-primary text-primary-foreground shadow-sm' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon 
                          name={section.icon} 
                          size={18} 
                          className={activeSection === section.id ? 'text-primary-foreground' : 'text-current'}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${activeSection === section.id ? 'text-primary-foreground' : 'text-current'}`}>
                            {section.label}
                          </p>
                          <p className={`text-xs ${activeSection === section.id ? 'text-primary-foreground/80' : 'text-muted-foreground'} truncate`}>
                            {section.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Settings Navigation - Mobile Tabs */}
            <div className="lg:hidden col-span-1">
              <div className="bg-card rounded-lg border border-border p-2 security-shadow-card mb-6">
                <div className="flex overflow-x-auto space-x-1 pb-2">
                  {settingSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id)}
                      className={`
                        flex-shrink-0 flex items-center space-x-2 px-4 py-2 rounded-md security-transition
                        ${activeSection === section.id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                    >
                      <Icon 
                        name={section.icon} 
                        size={16} 
                        className={activeSection === section.id ? 'text-primary-foreground' : 'text-current'}
                      />
                      <span className={`text-sm font-medium whitespace-nowrap ${activeSection === section.id ? 'text-primary-foreground' : 'text-current'}`}>
                        {section.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Settings Content */}
            <div className="lg:col-span-3">
              {ActiveComponent && <ActiveComponent />}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-lg border border-border p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={20} className="text-success" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">Security Status</h4>
                  <p className="text-xs text-success">All systems secure</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <Icon name="Smartphone" size={20} className="text-accent" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">Active Devices</h4>
                  <p className="text-xs text-muted-foreground">6 devices online</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-4 security-shadow-card">
              <div className="flex items-center space-x-3">
                <Icon name="Activity" size={20} className="text-warning" />
                <div>
                  <h4 className="text-sm font-medium text-foreground">System Health</h4>
                  <p className="text-xs text-muted-foreground">All services operational</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <BottomTabNavigation />
    </div>
  );
};

export default SettingsPage;